import type {
  BritProtocol,
  BritStudyContent,
  BritStudyContentElement,
  BritProtocolStep,
  BritProtocolScreen,
  BritProtocolScreenView,
  BritWindowContent,
  BritIdentifier,
  StudyRole,
  ScreenRole as ApiScreenRole,
} from '../../../api/types';

// ---------------------------------------------------------------------------
// OHIF v3 Hanging Protocol types
// ---------------------------------------------------------------------------

export interface OHIFHangingProtocol {
  id: string;
  locked: boolean;
  name: string;
  createdDate: string;
  modifiedDate: string;
  availableTo: Record<string, never>;
  editableBy: Record<string, never>;
  protocolMatchingRules: OHIFMatchingRule[];
  stages: OHIFStage[];
  numberOfPriorsReferenced: number;
}

export interface OHIFMatchingRule {
  id?: string;
  weight: number;
  attribute: string;
  constraint: Record<string, unknown>;
  required?: boolean;
}

export interface OHIFStage {
  id: string;
  name: string;
  viewportStructure: {
    layoutType: string;
    properties: { rows: number; columns: number };
  };
  viewports: OHIFViewport[];
}

export interface OHIFViewport {
  viewportOptions: { viewportType: string; toolGroupId: string };
  displaySets: OHIFDisplaySet[];
  displaySetOptions?: Array<{ voi?: { windowWidth: number; windowCenter: number } }>;
}

export interface OHIFDisplaySet {
  id?: string;
  seriesMatchingRules?: OHIFMatchingRule[];
  initialImageOptions?: { index: number };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Translate a Brit PACS canonical protocol into an OHIF v3 hanging protocol.
 */
export function britToOhifProtocol(brit: BritProtocol): OHIFHangingProtocol {
  const now = new Date().toISOString();

  return {
    id: `britpacs-${brit.id}`,
    locked: true,
    name: brit.name,
    createdDate: now,
    modifiedDate: now,
    availableTo: {},
    editableBy: {},
    protocolMatchingRules: translateMatchingRules(brit.content),
    stages: brit.steps
      .map((step) => translateStep(step))
      .filter((stage): stage is OHIFStage => stage !== null),
    numberOfPriorsReferenced: countComparisons(brit),
  };
}

/**
 * Translate a Brit protocol for a specific backend screen role (READING1, READING2, etc.).
 *
 * Unlike `britToOhifProtocol` which always picks READING1, this function
 * extracts the layout for the requested screen role from each step.
 * If a step doesn't have the requested screen, that step is omitted.
 */
export function britToOhifProtocolForScreen(
  brit: BritProtocol,
  targetScreen: ApiScreenRole,
): OHIFHangingProtocol | null {
  const now = new Date().toISOString();

  const stages = brit.steps
    .map((step) => translateStep(step, targetScreen))
    .filter((stage): stage is OHIFStage => stage !== null);

  if (stages.length === 0) return null;

  return {
    id: `britpacs-${brit.id}__${targetScreen}`,
    locked: true,
    name: `${brit.name} (${targetScreen})`,
    createdDate: now,
    modifiedDate: now,
    availableTo: {},
    editableBy: {},
    protocolMatchingRules: translateMatchingRules(brit.content),
    stages,
    numberOfPriorsReferenced: countComparisons(brit),
  };
}

/**
 * Return the set of backend screen roles (READING1, READING2, etc.)
 * that a protocol references across all its steps.
 */
export function getProtocolScreenRoles(brit: BritProtocol): ApiScreenRole[] {
  const roles = new Set<ApiScreenRole>();
  for (const step of brit.steps) {
    for (const role of Object.keys(step.screens) as ApiScreenRole[]) {
      if (step.screens[role]) roles.add(role);
    }
  }
  return Array.from(roles);
}

/**
 * Returns true when at least one step references more than one backend
 * screen role (e.g. both READING1 and READING2).
 */
export function isMultiScreenBritProtocol(brit: BritProtocol): boolean {
  return getProtocolScreenRoles(brit).length > 1;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Build OHIF study-level matching rules from the Brit content definition.
 * Only STUDY-type elements contribute identifiers; others are ignored.
 */
function translateMatchingRules(content: BritStudyContent): OHIFMatchingRule[] {
  const rules: OHIFMatchingRule[] = [];

  for (const element of content.elements) {
    if (element.ctype !== 'STUDY') continue;

    for (const ident of element.identifiers) {
      const rule = identifierToStudyRule(ident);
      if (rule) rules.push(rule);
    }
  }

  return rules;
}

/** Map a single Brit identifier to an OHIF study-level matching rule. */
function identifierToStudyRule(ident: BritIdentifier): OHIFMatchingRule | null {
  const val = ident.val;

  switch (ident.type) {
    case 'MODALITY':
      return val
        ? { attribute: 'ModalitiesInStudy', constraint: { contains: [val] }, required: true, weight: 2 }
        : null;

    case 'BODYPART':
      return val
        ? { attribute: 'BodyPartExamined', constraint: { equals: val }, weight: 1 }
        : null;

    case 'STUDYDESCRIPTION':
      return val
        ? { attribute: 'StudyDescription', constraint: { containsI: val }, weight: 1 }
        : null;

    default:
      return null;
  }
}

/**
 * Translate one Brit protocol step into an OHIF stage.
 *
 * When `targetScreen` is provided, uses that specific screen role.
 * Otherwise falls back to READING1, then the first screen found.
 * Within that screen, the TARGET view (or first view) drives the layout.
 *
 * Returns null if the target screen doesn't exist in this step.
 */
function translateStep(step: BritProtocolStep, targetScreen?: ApiScreenRole): OHIFStage | null {
  const screen = targetScreen
    ? step.screens[targetScreen]
    : pickScreen(step);

  if (!screen) return targetScreen ? null : {
    id: step.name,
    name: step.name,
    viewportStructure: { layoutType: 'grid', properties: { rows: 1, columns: 1 } },
    viewports: [],
  };

  const view = pickTargetView(screen);

  return {
    id: step.name,
    name: step.name,
    viewportStructure: {
      layoutType: 'grid',
      properties: {
        rows: view?.rows ?? 1,
        columns: view?.cols ?? 1,
      },
    },
    viewports: view ? view.wins.map(translateWindowContent) : [],
  };
}

/** Pick the best screen from a step: READING1 preferred, else first. */
function pickScreen(step: BritProtocolStep): BritProtocolScreen | undefined {
  if (step.screens.READING1) return step.screens.READING1;

  const keys = Object.keys(step.screens) as Array<keyof typeof step.screens>;
  return keys.length > 0 ? step.screens[keys[0]] : undefined;
}

/** Pick the TARGET view from a screen, or fall back to the first view. */
function pickTargetView(
  screen: BritProtocolScreen | undefined,
): BritProtocolScreenView | undefined {
  if (!screen || screen.views.length === 0) return undefined;

  const target = screen.views.find((v) => v.role === 'TARGET');
  return target ?? screen.views[0];
}

/**
 * Translate a single Brit window-content cell into an OHIF viewport.
 */
function translateWindowContent(win: BritWindowContent): OHIFViewport {
  const displaySet: OHIFDisplaySet = {};

  // Series-level matching rules from content identifiers
  const seriesRules = buildSeriesRules(win.content);
  if (seriesRules.length > 0) {
    displaySet.seriesMatchingRules = seriesRules;
  }

  // Image offset → initialImageOptions
  if (win.offset > 0) {
    displaySet.initialImageOptions = { index: win.offset };
  }

  const viewport: OHIFViewport = {
    viewportOptions: { viewportType: 'stack', toolGroupId: 'default' },
    displaySets: [displaySet],
  };

  // Window / level → VOI display set option
  if (win.window != null && win.level != null) {
    viewport.displaySetOptions = [
      { voi: { windowWidth: win.window, windowCenter: win.level } },
    ];
  }

  return viewport;
}

/** Convert Brit content-element identifiers into OHIF series matching rules. */
function buildSeriesRules(element: BritStudyContentElement): OHIFMatchingRule[] {
  const rules: OHIFMatchingRule[] = [];

  for (const ident of element.identifiers) {
    const rule = identifierToSeriesRule(ident);
    if (rule) rules.push(rule);
  }

  return rules;
}

/** Map a single Brit identifier to an OHIF series-level matching rule. */
function identifierToSeriesRule(ident: BritIdentifier): OHIFMatchingRule | null {
  const val = ident.val;

  switch (ident.type) {
    case 'SERIESUID':
      return val
        ? { attribute: 'SeriesInstanceUID', constraint: { equals: val }, weight: 3, required: true }
        : null;

    case 'SERIESDESCRIPTION':
      return val
        ? { attribute: 'SeriesDescription', constraint: { containsI: val }, weight: 1 }
        : null;

    case 'SERIESNUM':
      return val
        ? { attribute: 'SeriesNumber', constraint: { equals: parseInt(val, 10) }, weight: 1 }
        : null;

    case 'SERIESPLANE':
      return val
        ? { attribute: 'SeriesDescription', constraint: { containsI: val.toLowerCase() }, weight: 1 }
        : null;

    case 'SCOUT':
      return { attribute: 'SeriesDescription', constraint: { containsI: 'scout' }, weight: 1 };

    default:
      return null;
  }
}

/** Comparison roles that indicate a prior study reference. */
const COMPARISON_ROLES: ReadonlySet<StudyRole> = new Set([
  'COMPARISON1',
  'COMPARISON2',
  'COMPARISON3',
  'COMPARISON4',
]);

/**
 * Count the number of distinct comparison study roles referenced across all
 * steps, screens, and views in a protocol.
 */
function countComparisons(brit: BritProtocol): number {
  const seen = new Set<StudyRole>();

  for (const step of brit.steps) {
    for (const screen of Object.values(step.screens)) {
      if (!screen) continue;
      for (const view of screen.views) {
        if (COMPARISON_ROLES.has(view.role)) {
          seen.add(view.role);
        }
      }
    }
  }

  return seen.size;
}
